/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import ActionModel from 'cio/models/action';
import { isCustomPush, isCustomPushPayloadEmpty } from 'cio/utils/push/custom-payload';
import AnalyticsService from 'cio/services/analytics';
import EnvironmentService from 'cio/services/environment';
import { dropTask, task, timeout } from 'ember-concurrency';
import TemplateModel from 'cio/models/template';
import { isVisualEditorInAppMessage } from 'cio/utils/in-app/message-config';
import { tracked } from '@glimmer/tracking';

const DELAY_TIME_MS = 350;
const DEFAULT_ACTIONS = ['email_action'];
const INJECT_BODY_MODIFIER_ACTIONS = ['in_app_action', 'email_action'];
const TEMPLATE_REQUIRED_ACTIONS = ['in_app_action', 'push_action'];

// Why? `pointer-events:none;` allows scrolling on Chrome but not Firefox so we're going
// with a content mask over the iframe body to prevent link clicks, etc. on the preview
const BODY_CONTENT_MASK = `
  <style>
    body { position:relative; }
    body:before { display:block;content:'';height:100%;width:100%;position:absolute;z-index:1; }
  </style>
`;

interface ContentPreviewSignature {
  Args: {
    action: ActionModel;
    disablePopover?: boolean;
    hasReadOnlyPermission: boolean;
  };
  Element: HTMLDivElement;
}

export default class ContentPreview extends Component<ContentPreviewSignature> {
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;

  @tracked template: TemplateModel | null = null;

  popover: any | null;

  constructor(owner: unknown, args: ContentPreviewSignature['Args']) {
    super(owner, args);
    if (TEMPLATE_REQUIRED_ACTIONS.includes(args.action.type)) {
      this.fetchTemplateTask.perform();
    }
  }

  fetchTemplateTask = dropTask(async () => {
    if (this.args.action.templateId == null) {
      return;
    }

    let templateRef = this.args.action.belongsTo('template');
    let template = templateRef.value() as TemplateModel;
    if (template == null) {
      template = await templateRef.reload();
    }

    this.template = template;
  });

  get shouldInjectBodyModifier() {
    return INJECT_BODY_MODIFIER_ACTIONS.includes(this.args.action.type);
  }

  @computed('args.action.id')
  get isCopiedAction() {
    return this.args.action.id?.startsWith('copy_') || this.args.action.id?.startsWith('-');
  }

  @computed('environment.current.enabledActions.[]', 'args.action.type', 'template')
  get isActionTypeEnabled() {
    let { action } = this.args;
    let actionType = action.type;

    if (actionType === 'in_app_action') {
      return this.template != null && isVisualEditorInAppMessage(this.template);
    }

    if (DEFAULT_ACTIONS.includes(actionType)) {
      return true;
    }

    let actionChannel = actionType.replace(/_action$/, '');
    return this.environment.current?.enabledActions?.includes(actionChannel);
  }

  @computed('args.action.info', 'template')
  get hasIncompleteContent() {
    let { action } = this.args;

    if (this.template != null && isCustomPush(this.template)) {
      return isCustomPushPayloadEmpty(this.template);
    }

    if (action.type === 'webhook_action') {
      return action.hasNoRequest;
    }

    return action.hasNoContent;
  }

  @action
  registerPopover(_element: HTMLElement, popover: any[]) {
    if (popover.length > 0) {
      this.popover = popover[0];
    }
  }

  @action
  onMouseenterThumbnail() {
    if (this.args.disablePopover === true) {
      return;
    }

    this.popoverTimeout.perform();
  }

  @action
  onMouseleaveThumbnail() {
    this.popoverTimeout.cancelAll();
    this.popover?.close();
  }

  popoverTimeout = task({ restartable: true }, async () => {
    if (this.popover == null) {
      return;
    }

    await timeout(DELAY_TIME_MS);

    this.popover.open();
    this.trackHover();
  });

  @action
  trackHover() {
    this.analytics.track('campaign', 'workflow_existing_thumbnail_hovered', {
      action: this.args.action,
      model: this.args.action.campaign,
    });
  }

  @action
  trackLinkClick() {
    this.analytics.track('campaign', 'workflow_existing_item_preview_clicked', {
      action: this.args.action,
      model: this.args.action.campaign,
    });
  }

  injectFrameContentMask(body: HTMLElement) {
    body.insertAdjacentHTML('afterbegin', BODY_CONTENT_MASK); // eslint-disable-line spellcheck/spell-checker
    return body;
  }
}
